/**
 * Google Identity Services token flow for browser SPAs.
 * Loads the GIS script on demand, manages an in-memory token client,
 * persists tokens in sessionStorage, and renews silently when possible.
 */
const GIS_URL = 'https://accounts.google.com/gsi/client';
const TOKEN_KEY = 'skjv-storage-google-token';
export const DEFAULT_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/userinfo.email';
let gisLoadPromise = null;
export function ensureGisLoaded() {
    if (typeof window === 'undefined')
        return Promise.reject(new Error('No window'));
    if (window.google?.accounts?.oauth2)
        return Promise.resolve();
    if (gisLoadPromise)
        return gisLoadPromise;
    gisLoadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${GIS_URL}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('GIS load failed')), { once: true });
            return;
        }
        const script = document.createElement('script');
        script.src = GIS_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('GIS load failed'));
        document.head.appendChild(script);
    });
    return gisLoadPromise;
}
export function loadStoredToken() {
    if (typeof sessionStorage === 'undefined')
        return null;
    const raw = sessionStorage.getItem(TOKEN_KEY);
    if (!raw)
        return null;
    try {
        const t = JSON.parse(raw);
        if (t.expiresAt <= Date.now())
            return null;
        return t;
    }
    catch {
        return null;
    }
}
export function storeToken(t) {
    if (typeof sessionStorage === 'undefined')
        return;
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t));
}
export function clearStoredToken() {
    if (typeof sessionStorage === 'undefined')
        return;
    sessionStorage.removeItem(TOKEN_KEY);
}
export async function requestGoogleToken(clientId, scope, opts = {}) {
    await ensureGisLoaded();
    const oauth2 = window.google?.accounts.oauth2;
    if (!oauth2)
        throw new Error('GIS oauth2 unavailable');
    // An explicit (non-silent) auth request resets the silent-renew breaker so
    // the next save can attempt silent renewal again.
    if (opts.prompt !== 'none')
        silentRenewFailedThisSession = false;
    return new Promise((resolve, reject) => {
        const client = oauth2.initTokenClient({
            client_id: clientId,
            scope,
            callback: (resp) => {
                if (resp.error) {
                    reject(new Error(resp.error));
                    return;
                }
                const token = {
                    accessToken: resp.access_token,
                    expiresAt: Date.now() + resp.expires_in * 1000,
                    scope: resp.scope,
                };
                if (!tokenCoversScope(token, scope)) {
                    reject(new Error(`Google granted scope "${resp.scope}" but the app requested "${scope}". ` +
                        `Revoke access at https://myaccount.google.com/permissions and re-connect.`));
                    return;
                }
                storeToken(token);
                resolve(token);
            },
            error_callback: (err) => reject(err),
        });
        client.requestAccessToken({ prompt: opts.prompt ?? '' });
    });
}
/**
 * Per-session circuit breaker — if silent renew failed once, don't keep
 * popping up Google's auth iframe on every save. User explicitly clicking
 * Connect (which calls `requestGoogleToken` directly) resets it.
 */
let silentRenewFailedThisSession = false;
export function resetSilentRenewState() {
    silentRenewFailedThisSession = false;
}
export async function getValidToken(clientId, scope) {
    const stored = loadStoredToken();
    if (stored && tokenCoversScope(stored, scope))
        return stored;
    if (silentRenewFailedThisSession)
        return null;
    try {
        return await requestGoogleToken(clientId, scope, { prompt: 'none' });
    }
    catch {
        silentRenewFailedThisSession = true;
        return null;
    }
}
function tokenCoversScope(token, requested) {
    const have = new Set(token.scope.split(' '));
    return requested
        .split(' ')
        .filter((s) => s)
        .every((s) => have.has(s));
}
export async function revokeGoogleToken(token) {
    await ensureGisLoaded();
    return new Promise((resolve) => {
        window.google?.accounts.oauth2.revoke(token, () => resolve());
    });
}
//# sourceMappingURL=oauth-gis.js.map